/**
 * Invoices list (S11) — full surface.
 *
 * Composes the focused `features/invoices/list/` pieces:
 *   - KPI strip
 *   - Status tabs
 *   - Filter bar (search + customer + date)
 *   - Quick-filter chips
 *   - Bulk action bar (visible on selection)
 *   - Table with kebab actions per row
 *
 * Lifecycle dialogs (void) live here at the route — the table just
 * fires callbacks, the route owns the modal state.
 *
 * @see prototype/sales/invoices.html — V1.1 baseline (S11)
 * @see docs/spec/10-sales-invoicing-spec.md §12 — list behaviour
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { hasPermission } from "@flint/api-contract";
import { Alert, ConfirmDialog } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { VoidInvoiceDialog, formatLakhCrore } from "../../../../features/invoices/index.ts";
import {
  consumeInvoiceListFlash,
  type InvoiceListFlash,
} from "../../../../features/invoices/lib/list-flash.ts";
import {
  loadSavedViews,
  saveView,
  deleteView,
  type SavedInvoiceView,
} from "../../../../features/invoices/list/saved-views.ts";
import {
  KpiStrip,
  StatusTabs,
  FilterBar,
  QuickFilterChips,
  InvoiceTable,
  BulkActionBar,
  TopExposureCard,
  pickTopExposure,
  InvoiceListHeader,
  InvoiceInsights,
  type InvoiceTableRow,
  type StatusFilter,
  type QuickFilter,
} from "../../../../features/invoices/list/index.ts";
import type { CustomerSummary } from "../../../../features/invoices/customer-picker.tsx";

// Accept `?customerId=<uuid>` so customer-detail's "View all N" and
// similar affordances land on the invoices list with the customer
// filter chip preselected. Same shape used on `invoices/new.tsx`.
// Unknown / malformed values are dropped rather than 404-ing.
const searchSchema = z.object({
  customerId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/(workspace)/sales/invoices/")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "invoices.view")) {
      throw notFound();
    }
  },
  validateSearch: searchSchema,
  component: InvoicesList,
});

function InvoicesList() {
  const canCreate = usePermission("invoices.create");
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const me = useQuery(meQueryOptions);

  const { customerId: preselectCustomerId } = Route.useSearch();

  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [amountMin, setAmountMin] = useState<string | null>(null);
  const [amountMax, setAmountMax] = useState<string | null>(null);
  const [quick, setQuick] = useState<QuickFilter>(null);
  const [savedViews, setSavedViews] = useState<SavedInvoiceView[]>([]);
  const [activeSavedViewId, setActiveSavedViewId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [voidTarget, setVoidTarget] = useState<InvoiceTableRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InvoiceTableRow | null>(null);

  // Preselect a customer when the caller (customer-detail's "View all N",
  // or any deep link) provides `?customerId=<id>`. Mirrors the composer's
  // AC17 preselect pattern — one-shot fetch + adapt to `CustomerSummary`,
  // guarded so a background refetch can't clobber a later manual pick.
  const preselectQuery = trpc.customers.get.useQuery(
    { id: preselectCustomerId ?? "" },
    { enabled: !!preselectCustomerId && customer === null },
  );
  const preselectHydratedRef = useRef(false);
  useEffect(() => {
    if (preselectHydratedRef.current) return;
    if (!preselectQuery.data) return;
    const c = preselectQuery.data;
    preselectHydratedRef.current = true;
    setCustomer({
      id: c.id,
      name: c.name,
      contactName: c.contactName,
      gstin: c.gstin,
      phone: c.phone,
      state: c.state,
      city: c.city,
      paymentTerms: c.paymentTerms,
      creditLimitPaise: c.creditLimitPaise,
    });
  }, [preselectQuery.data]);

  // Flash banner — read on mount from sessionStorage (set by the composer
  // when it explicitly saves / deletes a draft and navigates here).
  // Auto-dismisses after 4s so the operator's eye lands on the list, not
  // the banner.
  const [flash, setFlash] = useState<InvoiceListFlash | null>(null);
  useEffect(() => {
    const value = consumeInvoiceListFlash();
    if (!value) return;
    setFlash(value);
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, []);

  // Saved views — hydrate from localStorage once me() resolves.
  // Views are per-user + per-company so multi-tenant machines don't
  // leak view names between workspaces.
  const companyId = me.data?.company?.id ?? null;
  const userId = me.data?.user.id ?? null;
  useEffect(() => {
    if (!companyId || !userId) return;
    setSavedViews(loadSavedViews(companyId, userId));
  }, [companyId, userId]);

  // Applying a saved view means dropping every filter into the
  // view's snapshot; the customer picker chip's label re-renders on
  // the next round-trip when `preselectQuery` hydrates from cache.
  const handleApplySavedView = useCallback(
    (v: SavedInvoiceView) => {
      setStatus(v.status);
      setQuick(v.quick);
      setSearch(v.search);
      setDateFrom(v.dateFrom);
      setDateTo(v.dateTo);
      setAmountMin(v.amountMin);
      setAmountMax(v.amountMax);
      setActiveSavedViewId(v.id);
      // Customer chip label needs the summary shape, not just id.
      // We clear when the view has no customer scope; a mismatched
      // pre-existing customer chip is fine — the operator can clear
      // it, and the customer id in the view is authoritative.
      if (!v.customerId) setCustomer(null);
    },
    [],
  );

  const handleSaveCurrentView = useCallback(() => {
    if (!companyId || !userId) return;
    const name = window.prompt("Name this view (e.g. 'Overdue > ₹50k')");
    if (!name || !name.trim()) return;
    try {
      const updated = saveView(companyId, userId, name.trim(), {
        status,
        quick,
        search,
        customerId: customer?.id ?? null,
        dateFrom,
        dateTo,
        amountMin,
        amountMax,
      });
      setSavedViews(updated);
      // The first entry in the updated array is the newly-saved view.
      const first = updated[0];
      if (first) setActiveSavedViewId(first.id);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Couldn't save the view.");
    }
  }, [
    companyId,
    userId,
    status,
    quick,
    search,
    customer,
    dateFrom,
    dateTo,
    amountMin,
    amountMax,
  ]);

  const handleDeleteSavedView = useCallback(
    (viewId: string) => {
      if (!companyId || !userId) return;
      const updated = deleteView(companyId, userId, viewId);
      setSavedViews(updated);
      if (activeSavedViewId === viewId) setActiveSavedViewId(null);
    },
    [companyId, userId, activeSavedViewId],
  );

  // Quick-filter chips translate into status / dateFrom changes that
  // override the explicit filters. Keep the translation here so the
  // child stays presentation-only.
  // 'all' is the tab id, never a list-query filter — drop it on the way out.
  type ListStatus = Exclude<StatusFilter, "all">;
  const effectiveFilters = useMemo((): {
    status: ListStatus | undefined;
    dateFrom: string | null;
  } => {
    if (quick === "all-open") {
      // The list query takes one status; we approximate "all open" by
      // dropping the status filter entirely (the table already shows
      // every row). The KPI strip's `outstanding` reflects the proper
      // sum.
      return { status: undefined, dateFrom };
    }
    if (quick === "overdue-30") {
      return { status: "overdue", dateFrom };
    }
    if (quick === "this-week-sent") {
      const monday = startOfWeekIso();
      return { status: "issued", dateFrom: monday };
    }
    if (quick === "paid-this-month") {
      const first = startOfMonthIso();
      return { status: "paid", dateFrom: first };
    }
    return {
      status: status === "all" ? undefined : (status as ListStatus),
      dateFrom,
    };
  }, [quick, status, dateFrom]);

  // Two queries on purpose:
  //
  //   - `listForKpis` — TAB-INDEPENDENT. Drives the page-header KPI strip,
  //     status-tab counts, lead summary, and the Top-exposure hero. Has no
  //     `status` filter, so the server's count/kpi aggregators (which already
  //     ignore status by design — see `loadKpis` + `countByStatus` in
  //     `invoices-queries.ts`) return identical values regardless of which
  //     tab is active. The query key stays stable across tab switches → no
  //     refetch, no re-render, no flicker on the strip.
  //
  //   - `list` — TAB-SPECIFIC. Drives only the table body. `placeholderData:
  //     (prev) => prev` keeps the previous rows visible while the new
  //     status-filtered set loads, so the table doesn't briefly blank.
  //
  // Both queries hit the same procedure; TanStack Query keys them by the
  // input object, so the two calls coexist independently in the cache.
  const listForKpis = trpc.invoices.list.useQuery({
    limit: 50,
    ...(customer ? { customerId: customer.id } : {}),
    ...(effectiveFilters.dateFrom ? { dateFrom: effectiveFilters.dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  });

  const list = trpc.invoices.list.useQuery(
    {
      limit: 50,
      ...(effectiveFilters.status ? { status: effectiveFilters.status } : {}),
      ...(customer ? { customerId: customer.id } : {}),
      ...(effectiveFilters.dateFrom ? { dateFrom: effectiveFilters.dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const send = trpc.invoices.send.useMutation();
  const clone = trpc.invoices.clone.useMutation({
    onSuccess: async (data) => {
      await utils.invoices.list.invalidate();
      void navigate({ to: "/sales/invoices/$invoiceId", params: { invoiceId: data.invoice.id } });
    },
  });
  const voidIt = trpc.invoices.void.useMutation({
    onSuccess: async () => {
      await utils.invoices.list.invalidate();
      setVoidTarget(null);
    },
  });
  const deleteDraft = trpc.invoices.deleteDraft.useMutation({
    onSuccess: async () => {
      await utils.invoices.list.invalidate();
      setDeleteTarget(null);
    },
  });

  const isFounder = me.data?.company?.role === "Founder";
  const items: InvoiceTableRow[] = useMemo(() => {
    const afterQuick = applyQuickFilter(list.data?.items ?? [], quick);
    if (amountMin === null && amountMax === null) return afterQuick;
    // Amount filter operates on the *net* payable (total − credited),
    // matching what the operator sees in the table's "Amount" column
    // and in the Export CSV net column.
    const minPaise = amountMin ? BigInt(Math.round(Number(amountMin) * 100)) : null;
    const maxPaise = amountMax ? BigInt(Math.round(Number(amountMax) * 100)) : null;
    return afterQuick.filter((r) => {
      const net = BigInt(r.totalPaise) - BigInt(r.creditedPaise || "0");
      if (minPaise !== null && net < minPaise) return false;
      if (maxPaise !== null && net > maxPaise) return false;
      return true;
    });
  }, [list.data?.items, quick, amountMin, amountMax]);

  const handleSendReminders = async () => {
    const targets = items.filter(
      (r) => selectedIds.has(r.id) && r.status !== "draft" && r.status !== "void",
    );
    for (const t of targets) {
      try {
        await send.mutateAsync({ invoiceId: t.id, channel: "whatsapp" });
      } catch {
        // continue — individual failures don't stop the batch.
      }
    }
    setSelectedIds(new Set());
  };

  // Hero "Top exposure" picks the oldest overdue invoice from the
  // tab-INDEPENDENT row set so it stays stable across tab switches.
  const topExposure = useMemo(
    () =>
      listForKpis.data
        ? pickTopExposure(listForKpis.data.items, listForKpis.data.kpis.overduePaise)
        : null,
    [listForKpis.data],
  );
  // Track the last-sent reminder so the hero can flash a quick
  // confirmation without piggybacking on the bottom toast.
  const [reminderJustSentFor, setReminderJustSentFor] = useState<string | null>(null);

  // ── Lead summary line under the H1 (mirrors the prototype) ──
  // Sourced from the tab-independent query so the summary doesn't flicker
  // when the operator changes tabs.
  const lead = listForKpis.data
    ? `${listForKpis.data.counts.all} total · ${formatLakhCrore(listForKpis.data.kpis.outstandingPaise)} outstanding · ${listForKpis.data.counts.overdue} overdue${
        listForKpis.data.kpis.oldestOverdueDays !== null && listForKpis.data.counts.overdue > 0
          ? ` · oldest ${listForKpis.data.kpis.oldestOverdueDays} days`
          : ""
      }`
    : null;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Sales" }, { label: "Invoices" }]}>
      <div className="flex flex-col gap-4">
        <InvoiceListHeader lead={lead} canCreate={canCreate} />

        {flash ? (
          <Alert intent="ok" description={flash.message} className="border-l-4 border-l-ok" />
        ) : null}

        {/* Hero "Top exposure" — only when there's an overdue invoice */}
        {topExposure ? (
          <TopExposureCard
            exposure={topExposure}
            reminderPending={send.isPending && send.variables?.invoiceId === topExposure.invoiceId}
            reminderJustSent={reminderJustSentFor === topExposure.invoiceId}
            onSendReminder={() => {
              setReminderJustSentFor(topExposure.invoiceId);
              send.mutate({ invoiceId: topExposure.invoiceId, channel: "whatsapp" });
            }}
          />
        ) : null}

        {/* KPI strip — driven by the tab-INDEPENDENT query. Stays stable
            across tab switches, no flicker. */}
        {listForKpis.data ? (
          <KpiStrip
            outstandingPaise={listForKpis.data.kpis.outstandingPaise}
            outstandingCustomerCount={listForKpis.data.kpis.outstandingCustomerCount}
            outstandingInvoiceCount={
              listForKpis.data.counts.issued +
              listForKpis.data.counts.partially_paid +
              listForKpis.data.counts.overdue
            }
            overduePaise={listForKpis.data.kpis.overduePaise}
            overdueInvoiceCount={listForKpis.data.counts.overdue}
            oldestOverdueDays={listForKpis.data.kpis.oldestOverdueDays}
            paidThisMonthPaise={listForKpis.data.kpis.paidThisMonthPaise}
            paidThisMonthInvoiceCount={listForKpis.data.counts.paid}
            draftCount={listForKpis.data.kpis.draftCount}
            outstanding7dDeltaPaise={listForKpis.data.kpis.outstanding7dDeltaPaise}
            paidLastMonthPaise={listForKpis.data.kpis.paidLastMonthPaise}
          />
        ) : null}

        {listForKpis.data ? <InvoiceInsights rows={items} kpis={listForKpis.data.kpis} /> : null}

        {/* Status tabs */}
        <StatusTabs
          value={status}
          onChange={(s) => {
            setStatus(s);
            setQuick(null);
          }}
          counts={{
            // Counts come from the tab-INDEPENDENT query so the tab badges
            // don't flicker when the operator switches between tabs.
            all: listForKpis.data?.counts.all ?? 0,
            draft: listForKpis.data?.counts.draft ?? 0,
            issued: listForKpis.data?.counts.issued ?? 0,
            partially_paid: listForKpis.data?.counts.partially_paid ?? 0,
            paid: listForKpis.data?.counts.paid ?? 0,
            overdue: listForKpis.data?.counts.overdue ?? 0,
            void: listForKpis.data?.counts.void ?? 0,
            reversed: listForKpis.data?.counts.reversed ?? 0,
          }}
        />

        {/* Quick filters */}
        <QuickFilterChips
          value={quick}
          onChange={setQuick}
          topExposure={topExposure}
          onTopExposureFilter={() => {
            if (!topExposure) return;
            setStatus("all");
            setQuick(null);
            setSearch(topExposure.customerName);
          }}
        />

        {/* Toolbar */}
        <FilterBar
          search={search}
          onSearchChange={(next) => {
            setSearch(next);
            setActiveSavedViewId(null);
          }}
          customer={customer}
          onCustomerChange={(next) => {
            setCustomer(next);
            setActiveSavedViewId(null);
          }}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateChange={({ from, to }) => {
            setDateFrom(from);
            setDateTo(to);
            setActiveSavedViewId(null);
          }}
          amountMin={amountMin}
          amountMax={amountMax}
          onAmountChange={({ min, max }) => {
            setAmountMin(min);
            setAmountMax(max);
            setActiveSavedViewId(null);
          }}
          exportDisabled={items.length === 0}
          onExport={() => {
            const stamp = new Date().toISOString().slice(0, 10);
            const scope = status === "all" ? "all" : status;
            const filename = `invoices-${scope}-${stamp}.xlsx`;
            void import("../../../../features/invoices/list/list-export.ts").then((m) =>
              m.downloadInvoiceXlsx(items, filename, {
                status,
                search,
                customerName: customer?.name ?? null,
                amountMin,
                amountMax,
                dateFrom,
                dateTo,
              }),
            );
          }}
          savedViews={savedViews}
          activeSavedViewId={activeSavedViewId}
          onApplySavedView={handleApplySavedView}
          onSaveCurrentView={handleSaveCurrentView}
          onDeleteSavedView={handleDeleteSavedView}
        />

        {/* Bulk action bar — only renders when at least one row is selected.
            An always-on bar reading "0 invoices selected" wastes vertical
            space and confuses the operator about whether action is expected. */}
        {selectedIds.size > 0 ? (
          <BulkActionBar
            selectedCount={selectedIds.size}
            onSendReminders={() => void handleSendReminders()}
            onClear={() => setSelectedIds(new Set())}
            busy={send.isPending}
          />
        ) : null}

        {/* Table / states */}
        {list.isLoading ? (
          <p className="text-sm text-muted">Loading invoices…</p>
        ) : list.error ? (
          <Alert intent="bad" title="Couldn't load invoices" description={list.error.message} />
        ) : items.length === 0 ? (
          <Alert
            intent="info"
            title="No invoices match"
            description="Try clearing filters, or create a new invoice."
          />
        ) : (
          <InvoiceTable
            rows={items}
            selectedIds={selectedIds}
            onToggleSelected={(id) =>
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onToggleSelectAll={(checked) => {
              if (checked) setSelectedIds(new Set(items.map((r) => r.id)));
              else setSelectedIds(new Set());
            }}
            isFounder={!!isFounder}
            onSend={(row) => send.mutate({ invoiceId: row.id, channel: "whatsapp" })}
            onClone={(row) => clone.mutate({ invoiceId: row.id })}
            onVoid={(row) => setVoidTarget(row)}
            onDelete={(row) => setDeleteTarget(row)}
            totalCount={
              // Reflects the active status tab when one is selected;
              // otherwise the full match count for "all".
              effectiveFilters.status
                ? (list.data?.counts[effectiveFilters.status] ?? items.length)
                : (list.data?.counts.all ?? items.length)
            }
          />
        )}

        <VoidInvoiceDialog
          open={voidTarget !== null}
          onOpenChange={(open) => {
            if (!open) setVoidTarget(null);
          }}
          docNumber={voidTarget?.docNumber ?? null}
          pending={voidIt.isPending}
          {...(voidIt.error ? { errorMessage: voidIt.error.message } : {})}
          onConfirm={(reason) => {
            if (!voidTarget) return;
            voidIt.mutate({ invoiceId: voidTarget.id, reason });
          }}
        />

        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          title="Delete this draft?"
          description={
            deleteTarget
              ? `Drafts can be safely deleted because no document number has been allocated. ${deleteTarget.customerName}'s draft will be removed permanently.`
              : "Drafts can be safely deleted because no document number has been allocated."
          }
          intent="danger"
          confirmLabel={deleteDraft.isPending ? "Deleting…" : "Delete draft"}
          onConfirm={() => {
            if (!deleteTarget) return;
            deleteDraft.mutate({ invoiceId: deleteTarget.id });
          }}
        />

        {deleteDraft.error ? (
          <Alert
            intent="bad"
            title="Couldn't delete draft"
            description={describeDeleteError(deleteDraft.error)}
          />
        ) : null}
      </div>
    </WorkspaceShell>
  );
}

/**
 * Swap raw domain / Zod error messages for plain-English copy on the list-
 * page delete-draft surface. Small local mapper — matches
 * `describeInvoiceMutationError` on the detail page + `describeComposerMutationError`
 * in the composer.
 */
function describeDeleteError(err: {
  data?: { code?: string | undefined } | undefined | null;
  message?: string | undefined | null;
} | null | undefined): string {
  if (!err) return "";
  const msg = err.message ?? "";
  if (/Invoice [0-9a-f-]{36} is (\w+); only drafts/i.test(msg)) {
    const status = msg.match(/is (\w+); only drafts/i)?.[1];
    return status
      ? `This invoice is ${status} — only drafts can be deleted.`
      : "This invoice is no longer a draft.";
  }
  switch (err.data?.code) {
    case "NOT_FOUND":
      return "The draft no longer exists.";
    case "PRECONDITION_FAILED":
      return "This invoice is no longer a draft.";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "You don't have permission to delete this draft.";
    default:
      return "Couldn't delete this draft. Try again in a moment.";
  }
}

// ── helpers ────────────────────────────────────────────────────────

/** Monday-of-this-week as `YYYY-MM-DD` (UTC). */
function startOfWeekIso(): string {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = (day + 6) % 7; // monday=0
  d.setUTCDate(d.getUTCDate() - diff);
  return d.toISOString().slice(0, 10);
}

/** First of this month as `YYYY-MM-DD` (UTC). */
function startOfMonthIso(): string {
  const d = new Date();
  d.setUTCDate(1);
  return d.toISOString().slice(0, 10);
}

function applyQuickFilter(rows: InvoiceTableRow[], quick: QuickFilter): InvoiceTableRow[] {
  if (quick === "all-open") {
    return rows.filter((r) => r.status !== "paid" && r.status !== "void");
  }
  if (quick === "overdue-30") {
    return rows.filter((r) => r.status === "overdue" && daysPastDue(r.dueOn) >= 30);
  }
  return rows;
}

function daysPastDue(dueOn: string): number {
  const due = new Date(`${dueOn}T00:00:00Z`);
  if (!Number.isFinite(due.getTime())) return 0;
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.max(0, Math.round((todayUtc - due.getTime()) / (1000 * 60 * 60 * 24)));
}
